import { lightColors } from "@certn/cascadia-tokens";

export const OPTIMAL_TEXT_COLOR_BLACK = lightColors["text-base"]; // #243134
export const OPTIMAL_TEXT_COLOR_WHITE = "#FFFFFF";

/**
 * Validates if a string is a valid hex color
 * @param color - The color string to validate
 * @returns True if the color is a valid hex color
 */
function validateHexColor(color: string): boolean {
  return /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(color);
}

/**
 * Normalizes a hex color to 6-digit format
 * @param color - The hex color (3 or 6 digits)
 * @returns The normalized 6-digit hex color
 */
function normalizeHexColor(color: string): string {
  if (!validateHexColor(color)) {
    throw new Error("Invalid hex color format");
  }

  // Remove the # if present
  const hex = color.replace("#", "");

  // Convert 3-digit to 6-digit
  if (hex.length === 3) {
    return `#${hex
      .split("")
      .map((char) => char + char)
      .join("")}`;
  }

  return `#${hex}`;
}

/**
 * Determines if a color is light or dark
 * @param color - The hex color to analyze
 * @returns True if the color is light, false if dark
 */
export function isLightColor(color: string): boolean {
  try {
    // Calculate relative luminance
    const hex = normalizeHexColor(color).substring(1);
    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);

    // Calculate relative luminance using sRGB formula
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance > 0.5;
  } catch {
    return true; // Default to light if calculation fails
  }
}

/**
 * Determines the appropriate text color (black or white) for a given background color
 * @param backgroundColor - The background color in hex format
 * @returns The text color that provides the best contrast
 */
export function getTextColorForBackground(backgroundColor: string): string {
  return isLightColor(backgroundColor)
    ? OPTIMAL_TEXT_COLOR_BLACK
    : OPTIMAL_TEXT_COLOR_WHITE;
}
